import { apiClient } from "./client";
import { Role, User } from "../types";

export async function listUsers(): Promise<{ users: User[] }> {
  const { data } = await apiClient.get<{ users: User[] }>("/users");
  return data;
}

export async function disableUser(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/users/${id}`);
  return data;
}

/** Riabilita un utente disabilitato (is_active = true). */
export async function reactivateUser(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.patch<{ success: true }>(`/users/${id}/reactivate`);
  return data;
}

/**
 * Eliminazione definitiva. Il backend risponde 409 con `dettaglio` (conteggio
 * dei record collegati) se l'utente non è eliminabile: quel corpo di errore
 * viene propagato così com'è e gestito dal chiamante.
 */
export async function deleteUserPermanent(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/users/${id}/permanent`);
  return data;
}

// --- Modifica anagrafica utente (solo admin) --------------------------------
// Il ruolo NON è modificabile: non deve mai comparire in questo payload.
export interface UpdateUserPayload {
  full_name?: string | null;
  email?: string;
  telefono?: string | null;
  note?: string | null;
  centro_assistenza_id?: string | null;
  istituto_vigilanza_id?: string | null;
  /** Credenziale con cui l'utente effettua il login. Il backend rifiuta il
   *  cambio se il canale scelto non è valorizzato (es. telefono vuoto). */
  login_method?: "email" | "telefono";
}

export async function updateUser(id: string, payload: UpdateUserPayload): Promise<{ user: User }> {
  const { data } = await apiClient.patch<{ user: User }>(`/users/${id}`, payload);
  return data;
}

export interface ResetPasswordResponse {
  success: true;
  /** Presente solo se sia email che SMS sono falliti: fallback da comunicare a mano. */
  temp_password?: string;
  notifica_inviata: { email: boolean; sms: boolean };
}

export async function resetPassword(id: string): Promise<ResetPasswordResponse> {
  const { data } = await apiClient.patch<ResetPasswordResponse>(`/users/${id}/reset-password`);
  return data;
}

// --- Flusso B: creazione diretta operatore -----------------------------------
export interface CreateOperatorPayload {
  nome: string;
  email?: string;
  telefono?: string;
  ruolo: "operatore_tecnico" | "operatore_vigilanza";
  parent_id?: string; // richiesto solo se il chiamante è admin
  // Rilevante solo se sono forniti SIA email che telefono: sceglie con quale
  // credenziale l'operatore accede. Con un solo canale il backend lo deduce.
  login_method?: "email" | "telefono";
}

export interface CreateOperatorResponse {
  user_id: string;
  credenziali_inviate: { email: boolean; sms: boolean };
  temp_password?: string; // presente solo se sia email che SMS sono falliti
}

export async function createOperator(payload: CreateOperatorPayload): Promise<CreateOperatorResponse> {
  const { data } = await apiClient.post<CreateOperatorResponse>("/users/create-operator", payload);
  return data;
}

// --- Flusso A: invito con auto-registrazione --------------------------------
export interface CreateInvitoPayload {
  email: string;
  role: Extract<Role, "admin" | "staff" | "centro_assistenza" | "istituto_vigilanza" | "cliente">;
  centro_assistenza_id?: string;
  istituto_vigilanza_id?: string;
  /** Solo per role = cliente (Fase 3.3): aziende dell'anagrafica da collegare. */
  cliente_ids?: string[];
}

export interface CreateInvitoResponse {
  success: true;
  email_sent: boolean;
  message: string;
  registration_link: string;
}

export async function createInvito(payload: CreateInvitoPayload): Promise<CreateInvitoResponse> {
  const { data } = await apiClient.post<CreateInvitoResponse>("/inviti", payload);
  return data;
}

/**
 * Un invito ancora in sospeso: una persona invitata che non si è ancora
 * registrata, e che quindi in `/users` non esiste. La lista Utenti li mostra
 * insieme agli utenti veri per non lasciare il buco fra l'invio e la
 * registrazione.
 */
export interface InvitoPending {
  id: string;
  email: string;
  role: Role;
  /** Centro Assistenza o Istituto di Vigilanza dell'invito, quando previsto dal ruolo. */
  organizzazione: string | null;
  /** Nome (o email) di chi ha mandato l'invito. */
  invitato_da: string | null;
  /** `false` = il record c'è ma l'email non è mai partita: nessuno ha ricevuto il link. */
  email_sent: boolean;
  expires_at: string;
  /** Scaduto ma ancora in sospeso: continua a occupare l'email finché non lo si ripete o annulla. */
  scaduto: boolean;
  created_at: string;
}

/** Inviti in sospeso (solo admin). */
export async function listInviti(): Promise<{ inviti: InvitoPending[] }> {
  const { data } = await apiClient.get<{ inviti: InvitoPending[] }>("/inviti");
  return data;
}

export interface ResendInvitoResponse {
  success: true;
  email_sent: boolean;
  expires_at: string;
  message: string;
  registration_link: string;
}

/** Ripete un invito: nuovo token (il link precedente smette di valere) e nuova scadenza. */
export async function resendInvito(id: string): Promise<ResendInvitoResponse> {
  const { data } = await apiClient.post<ResendInvitoResponse>(`/inviti/${id}/resend`);
  return data;
}

/** Annulla un invito in sospeso: il link non vale più e l'email torna invitabile. */
export async function cancelInvito(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/inviti/${id}`);
  return data;
}

export interface InvitoInfo {
  email: string;
  role: Role;
  expires_at: string;
}

/** Endpoint pubblico: verifica un token di invito prima di mostrare /register. */
export async function verifyInvito(token: string): Promise<InvitoInfo> {
  const { data } = await apiClient.get<InvitoInfo>(`/inviti/${token}`);
  return data;
}

// --- Aziende collegate a un utente cliente (Fase 3.3, solo admin) ------------

/** Cliente dell'anagrafica in forma ridotta (per select/tag). */
export interface ClienteLite {
  id: string;
  nome: string;
  insegna: string | null;
}

/** Ricerca clienti per il multi-select (typeahead, min 2 caratteri). */
export async function searchClienti(q: string): Promise<ClienteLite[]> {
  if (q.trim().length < 2) return [];
  const { data } = await apiClient.get<{ clienti: ClienteLite[] }>("/clienti-search", { params: { q } });
  return data.clienti;
}

/** Aziende attualmente collegate a un utente cliente. */
export async function getUserClienti(userId: string): Promise<ClienteLite[]> {
  const { data } = await apiClient.get<{ clienti: ClienteLite[] }>(`/users/${userId}/clienti`);
  return data.clienti;
}

/** Imposta l'elenco COMPLETO delle aziende collegate (diff lato backend). */
export async function setUserClienti(userId: string, cliente_ids: string[]): Promise<ClienteLite[]> {
  const { data } = await apiClient.put<{ clienti: ClienteLite[] }>(`/users/${userId}/clienti`, { cliente_ids });
  return data.clienti;
}

// --- Permessi: catalogo + override per utente (Fase 2.3/2.4, solo admin) -----

export interface PermissionOverride {
  id: string;
  user_id: string;
  permission_key: string;
  granted: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface PermissionsCatalog {
  /** Tutte le permission key valide (per il dropdown "aggiungi override"). */
  permissions: string[];
  /** Permessi di default per ruolo (mappa PERMISSION_ROLES). */
  role_defaults: Record<Role, string[]>;
}

/** Catalogo centralizzato dei permessi (chiavi valide + default per ruolo). */
export async function getPermissionsCatalog(): Promise<PermissionsCatalog> {
  const { data } = await apiClient.get<PermissionsCatalog>("/permissions");
  return data;
}

/** Override attivi su un utente. */
export async function listPermissionOverrides(userId: string): Promise<{ overrides: PermissionOverride[] }> {
  const { data } = await apiClient.get<{ overrides: PermissionOverride[] }>(`/users/${userId}/permission-overrides`);
  return data;
}

/** Crea o aggiorna un override (concede/toglie un permesso a un utente). */
export async function setPermissionOverride(
  userId: string,
  permission_key: string,
  granted: boolean
): Promise<{ override: PermissionOverride }> {
  const { data } = await apiClient.post<{ override: PermissionOverride }>(`/users/${userId}/permission-overrides`, {
    permission_key,
    granted,
  });
  return data;
}

/** Rimuove un override: l'utente torna al default del suo ruolo. */
export async function deletePermissionOverride(userId: string, permissionKey: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(
    `/users/${userId}/permission-overrides/${encodeURIComponent(permissionKey)}`
  );
  return data;
}
